import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { copyToClipboard, docsCallExamples, DocsLang, highlightJson, stripHighlighting } from '../../utils/docs-code';
import { DOCS_ENDPOINTS, DocsEndpoint, DocsParam, DocsSchema, schemasFor } from '../../data/docs-api-data';
import { OrgContextService } from '../../../../core/services';

/** Stands in for x-request-id in the Example section's "real backend
 *  response" trace (see traceHtml) deliberately not real- or
 *  UUID-looking, so it can't be mistaken for an actual logged request id
 *  a reader might otherwise think it's safe to search their own logs for. */
const TRACE_REQUEST_ID_PLACEHOLDER = '<unique request id>';

/** Which of sampleResponse.data's own fields is "the list" for a given
 *  endpoint snippets for rag, sources for answer, results for search
 *  each only shows 1-2 illustrative entries even though comprehensiveness/
 *  max_results allows far more back in reality; traceHtml appends a bare
 *  `...` after the last one so that reads as "there's more", not as the
 *  entire result set. */
const TRACE_LIST_FIELDS = ['snippets', 'sources', 'results'];

/** Placed as the list's last element, then swapped for a bare `...` (no
 *  quotes, no span) after highlighting chosen so it can't collide with
 *  real example content and won't partial-match anything else in the
 *  page's other JSON. */
const TRACE_MORE_MARKER = '@@DOCS_TRACE_MORE@@';

/**
 * One endpoint's own reference page (/docs/api-reference/:id rag,
 * answer, search, and whatever's added to DOCS_ENDPOINTS next). Replaces
 * the old DocsApiReference, which rendered all three endpoints (request
 * table, response table, example, schemas) on one long page keyed off
 * anchors; each endpoint now gets its own route and this one shared
 * component, driven entirely by DOCS_ENDPOINTS/DOCS_SCHEMAS (see
 * ../../data/docs-api-data) so a fourth endpoint needs a new data entry,
 * not a new component file.
 *
 * The route is matched by path shape alone (":id"), so Angular reuses this
 * same component instance when navigating rag → answer → search from the
 * sidebar rather than destroying and recreating it hence subscribing to
 * paramMap instead of reading it once in the constructor.
 */
@Component({
  selector: 'app-docs-api-reference-detail',
  standalone: false,
  templateUrl: './docs-api-reference-detail.html',
  styleUrl: './docs-api-reference-detail.scss',
})
export class DocsApiReferenceDetail implements OnInit, OnDestroy {
  readonly languages: { id: DocsLang; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'python', label: 'Python' },
  ];

  endpoint: DocsEndpoint | null = null;
  schemas: DocsSchema[] = [];

  private lang: DocsLang = 'curl';
  copied = false;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private orgContext: OrgContextService,
  ) {}

  /** Unlike the Playground pages, /docs isn't behind an authGuard (it's a
   *  public, unauthenticated route tree see docs-layout.ts's own doc
   *  comment), so there's no live SessionService.organization to read
   *  here and no /me call is made just to check this (docs stay a plain,
   *  no-network-dependency page for anonymous visitors too). Reads the
   *  same localStorage flag OrgContextService already persists org
   *  context to written whenever SessionService.loadMe() succeeds
   *  anywhere else in the app, cleared on logout so this is accurate
   *  for as long as *this browser* has been signed in to a Cikisi
   *  organization, without this page needing a network call of its own. */
  get isCikisiUser(): boolean {
    return this.orgContext.getIsCikisiUser();
  }

  /** endpoint.requestParams minus the Cikisi-only ones (see DocsParam's
   *  own doc comment) shown in the main Request body table so every
   *  reader sees only params they can actually use. */
  get generalRequestParams(): DocsParam[] {
    return this.endpoint!.requestParams.filter(p => !p.cikisiOnly);
  }

  /** The Cikisi-only params pulled out of Request body's own table
   *  shown in their own box, gated behind isCikisiUser above. */
  get cikisiRequestParams(): DocsParam[] {
    return this.endpoint!.requestParams.filter(p => p.cikisiOnly);
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this._destroy$)).subscribe(params => {
      const id = params.get('id');
      const found = DOCS_ENDPOINTS.find(ep => ep.id === id) ?? null;
      if (!found) {
        // Not a published endpoint id back to the list rather than a
        // dead page with nothing to show.
        this.router.navigate(['/docs/api-reference']);
        return;
      }
      this.endpoint = found;
      this.schemas = schemasFor(found.id);
      this.copied = false;
    });
  }

  setLang(lang: DocsLang): void {
    this.lang = lang;
  }

  activeLang(): DocsLang {
    return this.lang;
  }

  get exampleHtml(): SafeHtml {
    const examples = docsCallExamples(this.endpoint!.path, this.endpoint!.example);
    return this.sanitizer.bypassSecurityTrustHtml(examples[this.lang]);
  }

  /** The "real backend response" trace under the request example status
   *  and headers plus a populated body, not just the abstract shape the
   *  Response schemas table documents. */
  get traceHtml(): SafeHtml {
    const e = this.endpoint!;
    const data: Record<string, unknown> = { ...e.sampleResponse.data };
    for (const field of TRACE_LIST_FIELDS) {
      const list = data[field];
      if (Array.isArray(list)) {
        data[field] = [...list, TRACE_MORE_MARKER];
      }
    }
    const trace = {
      status: 200,
      headers: {
        'content-type': 'application/json; charset=utf-8',
        'x-request-id': TRACE_REQUEST_ID_PLACEHOLDER,
      },
      body: {
        data,
        messages: e.sampleResponse.messages,
        success: true,
      },
    };
    const html = highlightJson(trace)
      .split(`<span class="code-str">"${TRACE_MORE_MARKER}"</span>`)
      .join('...');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  async copyExample(): Promise<void> {
    const examples = docsCallExamples(this.endpoint!.path, this.endpoint!.example);
    if (!(await copyToClipboard(stripHighlighting(examples[this.lang])))) return;
    this.copied = true;
    setTimeout(() => { this.copied = false; }, 2000);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
